import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
} from '@chakra-ui/react';
import { useCallback, useEffect } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import {
  EstimationInput,
  EstimationPresets,
  parseEstimationInput,
} from '../App';
import { usesNeedsRoomInput } from '../configs/industryCalculationTemplate';
import { getIndustryLabelById } from '../configs/merchantConfigs';

type Props = {
  onSubmit: (inputValues: EstimationInput) => void;
  estimationPresets: EstimationPresets;
  countryCode: string;
};

type FormInputKey = keyof EstimationInput;

export const EstimationForm = ({
  onSubmit,
  estimationPresets,
  countryCode
}: Props): JSX.Element => {
  const {
    handleSubmit,
    errors,
    register,
    formState,
    setValue,
    watch,
  } = useForm<EstimationInput>();

  useEffect(() => {
    setValue('industryId', estimationPresets.industryId);
  }, [estimationPresets]);

  const { industryId } = watch(['industryId']);

  const onSubmitForm = (values: Record<string, string>) => {
    const estimationInput = parseEstimationInput(values);
    estimationInput.country = countryCode
    onSubmit(estimationInput);
  };

  const getErrorByKey = useCallback(
    (key: FormInputKey): FieldError | undefined => {
      const error: FieldError | undefined = errors && errors[key];
      return error;
    },
    [errors],
  );

  const amountError = getErrorByKey('employees');
  const roomError = getErrorByKey('rooms');

  const style = {
    Label: {
      fontSize: '12px',
      fontWeight: 700,
      letterSpacing: '2px',
      textTransform: 'uppercase',
      mb: '0',
      pb: '5',
    },
    Input: {
      padding: '16px 15px',
    },
  };

  return (
    <>
      <form noValidate onSubmit={handleSubmit(onSubmitForm)}>
        <FormControl isInvalid={!!getErrorByKey('industryId')} pb="5">
          <FormLabel sx={style.Label}>
            <Text as="span">Select industry</Text>
          </FormLabel>
          <Select name="industryId" size="lg" ref={register}>
            {estimationPresets.industryIds.map((industryId) => {
              return (
                <option key={industryId} value={industryId}>
                  {getIndustryLabelById(industryId)}
                </option>
              );
            })}
          </Select>

          <FormHelperText>
            <Text as="span">This is the industry you are working in</Text>
          </FormHelperText>
        </FormControl>

        <FormControl isInvalid={!!amountError} isRequired={true} mb="0" pb="5">
          <FormLabel sx={style.Label}>
            {industryId && (
              <Text as="span">
                {estimationPresets.emittorLabels[industryId].label}
              </Text>
            )}
          </FormLabel>

          <NumberInput size="lg" defaultValue={1} min={0}>
            <NumberInputField
              placeholder="e.g 5"
              name="employees"
              ref={register({
                validate: (value) =>
                  !!(value.length && value.length > 0) ||
                  'Please fill in an amount',
              })}
            />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>

          {/* FormControl needs to have `isInvalid` set to true in order to show */}
          <FormErrorMessage>
            {amountError ? amountError.message : ''}
          </FormErrorMessage>

          <FormHelperText>
            <Text as="span">Indicate the amount</Text>
          </FormHelperText>
        </FormControl>
        {usesNeedsRoomInput(industryId) && (
          <FormControl isInvalid={!!roomError} isRequired={true} pb="5">
            <FormLabel pb="5" sx={style.Label}>
              <Text as="span">
                Number of rooms
              </Text>
            </FormLabel>

            <>
              <NumberInput defaultValue={25} min={1}>
                <NumberInputField
                  placeholder="e.g 25"
                  name="rooms"
                  ref={register({
                    validate: (value) =>
                      !!(value.length && value.length > 0) ||
                      'Please fill in an amount',
                  })}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </>

            {/* FormControl needs to have `isInvalid` set to true in order to show */}
            <FormErrorMessage>
              {roomError ? roomError.message : ''}
            </FormErrorMessage>

            <FormHelperText>
              <Text as="span">Indicate the amount</Text>
            </FormHelperText>
          </FormControl>
        )}

        <Button isLoading={formState.isSubmitting} type="submit">
          Estimate emissions
        </Button>
      </form>
    </>
  );
};
